function _array_like_to_array(arr, len) {
    if (len == null || len > arr.length) len = arr.length;
    for(var i = 0, arr2 = new Array(len); i < len; i++)arr2[i] = arr[i];
    return arr2;
}
function _array_with_holes(arr) {
    if (Array.isArray(arr)) return arr;
}
function _iterable_to_array_limit(arr, i) {
    var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];
    if (_i == null) return;
    var _arr = [];
    var _n = true;
    var _d = false;
    var _s, _e;
    try {
        for(_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true){
            _arr.push(_s.value);
            if (i && _arr.length === i) break;
        }
    } catch (err) {
        _d = true;
        _e = err;
    } finally{
        try {
            if (!_n && _i["return"] != null) _i["return"]();
        } finally{
            if (_d) throw _e;
        }
    }
    return _arr;
}
function _non_iterable_rest() {
    throw new TypeError("Invalid attempt to destructure non-iterable instance.\\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _sliced_to_array(arr, i) {
    return _array_with_holes(arr) || _iterable_to_array_limit(arr, i) || _unsupported_iterable_to_array(arr, i) || _non_iterable_rest();
}
function _unsupported_iterable_to_array(o, minLen) {
    if (!o) return;
    if (typeof o === "string") return _array_like_to_array(o, minLen);
    var n = Object.prototype.toString.call(o).slice(8, -1);
    if (n === "Object" && o.constructor) n = o.constructor.name;
    if (n === "Map" || n === "Set") return Array.from(n);
    if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _array_like_to_array(o, minLen);
}
import * as React from 'react';
import { useGridSelector, useGridApiOptionHandler, gridVisibleColumnDefinitionsSelector, useGridApiMethod, gridDimensionsSelector } from '@mui/x-data-grid';
import { useGridVisibleRows, useTimeout, gridHorizontalScrollbarHeightSelector } from '@mui/x-data-grid/internals';
import useEventCallback from '@mui/utils/useEventCallback';
import { styled } from '@mui/system';
import { jsx as _jsx } from "react/jsx-runtime";
var InfiniteLoadingTriggerElement = styled('div')({
    position: 'sticky',
    left: 0,
    width: 0,
    height: 0
});
/**
 * @requires useGridColumns (state)
 * @requires useGridDimensions (method) - can be after
 * @requires useGridScroll (method
 */ export var useGridInfiniteLoader = function(apiRef, props) {
    var isReady = useGridSelector(apiRef, gridDimensionsSelector).isReady;
    var visibleColumns = useGridSelector(apiRef, gridVisibleColumnDefinitionsSelector);
    var currentPage = useGridVisibleRows(apiRef, props);
    var observer = React.useRef(null);
    var updateTargetTimeout = useTimeout();
    var triggerElement = React.useRef(null);
    var isEnabled = props.rowsLoadingMode === 'client' && !!props.onRowsScrollEnd;
    var handleLoadMoreRows = useEventCallback(function(param) {
        var _param = _sliced_to_array(param, 1), entry = _param[0];
        var currentRatio = entry.intersectionRatio;
        var isIntersecting = entry.isIntersecting;
        if (isIntersecting && currentRatio === 1) {
            var _observer_current;
            var viewportPageSize = apiRef.current.getViewportPageSize();
            var rowScrollEndParams = {
                visibleColumns: visibleColumns,
                viewportPageSize: viewportPageSize,
                visibleRowsCount: currentPage.rows.length
            };
            apiRef.current.publishEvent('rowsScrollEnd', rowScrollEndParams);
            (_observer_current = observer.current) === null || _observer_current === void 0 ? void 0 : _observer_current.disconnect();
            // do not observe this node anymore
            triggerElement.current = null;
        }
    });
    React.useEffect(function() {
        var _observer_current;
        var virtualScroller = apiRef.current.virtualScrollerRef.current;
        if (!isEnabled || !isReady || !virtualScroller) {
            return;
        }
        (_observer_current = observer.current) === null || _observer_current === void 0 ? void 0 : _observer_current.disconnect();
        var horizontalScrollbarHeight = gridHorizontalScrollbarHeightSelector(apiRef.current.state);
        var marginBottom = props.scrollEndThreshold - horizontalScrollbarHeight;
        observer.current = new IntersectionObserver(handleLoadMoreRows, {
            threshold: 1,
            root: virtualScroller,
            rootMargin: "0px 0px ".concat(marginBottom, "px 0px")
        });
        if (triggerElement.current) {
            observer.current.observe(triggerElement.current);
        }
    }, [
        apiRef,
        isReady,
        handleLoadMoreRows,
        isEnabled,
        props.scrollEndThreshold
    ]);
    var updateTarget = function(node) {
        if (triggerElement.current !== node) {
            var _observer_current;
            (_observer_current = observer.current) === null || _observer_current === void 0 ? void 0 : _observer_current.disconnect();
            triggerElement.current = node;
            if (triggerElement.current) {
                var _observer_current1;
                (_observer_current1 = observer.current) === null || _observer_current1 === void 0 ? void 0 : _observer_current1.observe(triggerElement.current);
            }
        }
    };
    var triggerRef = React.useCallback(function(node) {
        // Prevent the infite loading working in combination with lazy loading
        if (!isEnabled) {
            return;
        }
        // If the user scrolls through the grid too fast it might happen that the observer is connected to the trigger element
        // that will be intersecting the root inside the same render cycle (but not intersecting at the time of the connection).
        // This will cause the observer to not call the callback with `isIntersecting` set to `true`.
        // https://www.w3.org/TR/intersection-observer/#event-loop
        // Delaying the connection to the next cycle helps since the observer will always call the callback the first time it is connected.
        // https://developer.mozilla.org/en-US/docs/Web/API/IntersectionObserver/observe
        // Related to
        // https://github.com/mui/mui-x/issues/14116
        updateTargetTimeout.start(0, function() {
            return updateTarget(node);
        });
    }, [
        isEnabled,
        updateTargetTimeout
    ]);
    var getInfiniteLoadingTriggerElement = React.useCallback(function(param) {
        var lastRowId = param.lastRowId;
        if (!isEnabled) {
            return null;
        }
        return /*#__PURE__*/ _jsx(InfiniteLoadingTriggerElement, {
            ref: triggerRef,
            role: "presentation"
        }, "trigger-".concat(lastRowId));
    }, [
        isEnabled,
        triggerRef
    ]);
    var infiniteLoaderPrivateApi = {
        getInfiniteLoadingTriggerElement: getInfiniteLoadingTriggerElement
    };
    useGridApiMethod(apiRef, infiniteLoaderPrivateApi, 'private');
    useGridApiOptionHandler(apiRef, 'rowsScrollEnd', props.onRowsScrollEnd);
};
